import { getUserNotifications } from "../../notifications/services/notifications.service";
import PageFooter from "../../shared/app-shell/PageFooter";
import { notificationsAtom, themeAtom, userAtom } from "../../shared/atoms";
import DataGridToolbar from "../../shared/components/DataGridToolbar";
import Datepicker from "../../shared/components/Datepicker/Datepicker";
import Dropdown from "../../shared/components/Dropdown/Dropdown";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import Modal from "../../shared/components/ModalWindow";
import { ToastContainer, toastError, toastSuccess, toastWarning } from "../../shared/components/ToastContainer";
import WithTooltip from "../../shared/components/WithTooltip";
import { logE, log, logV } from "../../shared/services/debug-logger";
import HullCleaningRequestModal from "../components/HullCleaningRequestModal";
import UwOpsSections from "../components/UwOpsSections";
import type {
  HullCleaningEvent,
  HullCleaningOutcome,
  HullCleaningRequest,
  HullCleaningRequestBundle,
  HullCleaningSubOutcome,
  HullCleaningVendor,
} from "../models/hull-cleaning.models";
import { hullCleaningOutcomes, hullCleaningSubOutcomes } from "../models/hull-cleaning.models";
import {
  cancelHullCleaningRequest,
  confirmHullCleaningBooking,
  getActiveHullCleaningRequestBundleByVessel,
  getCompletedHullCleaningEventsByVessel,
  getCompletedHullCleaningRequestsByVessel,
  getHullCleaningRequestBundle,
  getHullCleaningVendors,
  submitHullCleaningOutcome,
  submitHullCleaningPlanning,
  updateHullCleaningRequest,
} from "../services/hull-cleaning.service";
import { getVendorOptionsForLocation } from "../utils/vendor.utils";
import { ArrowsUpDownIcon, ChevronDownIcon, ChevronUpIcon, PlusIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type Status = "open" | "planned" | "completed" | "cancelled";
type RecordMode = "active" | "completed";
type UwOpsRequestSortKey = "requestDate" | "cleaningDate" | "vesselName" | "status";

const uwOpsRequestColumns: Array<{ label: string; sortKey?: UwOpsRequestSortKey }> = [
  { label: "Status", sortKey: "status" },
  { label: "Vessel Name", sortKey: "vesselName" },
  { label: "Vessel Code" },
  { label: "IMO Number" },
  { label: "Ops Coordinator" },
  { label: "Request Date", sortKey: "requestDate" },
  { label: "Reason" },
  { label: "Perf. Team Comment" },
  { label: "Booked Date" },
  { label: "Booked Port" },
  { label: "VO Comment" },
  { label: "Cleaning Date", sortKey: "cleaningDate" },
  { label: "Cleaning Port" },
  { label: "Vendor" },
  { label: "Vendor Ref" },
  { label: "Outcome" },
];

const hullCleaningOutcome = "Hull Cleaning";
const isHullCleaningSubOutcome = (outcome: string): outcome is HullCleaningSubOutcome =>
  (hullCleaningSubOutcomes as readonly string[]).includes(outcome);

type FormState = {
  opsCoordinatorName: string;
  bookedDate: string;
  bookedPort: string;
  voComment: string;
  cleaningDate: string;
  cleaningPort: string;
  vendor: string;
  vendorRef: string;
  outcomeComment: string;
  outcomes: HullCleaningOutcome[];
  hullCleaningType: HullCleaningSubOutcome | "";
  cancelComment: string;
};

const toStatus = (value?: string | null): Status => {
  const normalized = (value ?? "").trim().toLowerCase();
  if (normalized === "planned") return "planned";
  if (normalized === "completed" || normalized === "complete") return "completed";
  if (normalized === "cancelled") return "cancelled";
  return "open";
};

const dateOnly = (value?: string | null) => (value ? value.slice(0, 10) : "");

const displayValue = (value?: string | null) => {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : "-";
};

const isEarlierDate = (left?: string | null, right?: string | null) => {
  if (!left || !right) return false;
  return left < right;
};

const toDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildForm = (request: HullCleaningRequest): FormState => ({
  opsCoordinatorName: request.opsCoordinatorName ?? "",
  bookedDate: dateOnly(request.cleaningDate),
  bookedPort: request.cleaningPort ?? "",
  voComment: request.bookingComment ?? "",
  cleaningDate: toStatus(request.status) === "completed" ? dateOnly(request.cleaningDate) : "",
  cleaningPort: toStatus(request.status) === "completed" ? request.cleaningPort ?? "" : "",
  vendor: request.vendor ?? "",
  vendorRef: request.vendorRef ?? "",
  outcomeComment: "",
  outcomes: request.outcomes.filter((outcome): outcome is HullCleaningOutcome =>
    (hullCleaningOutcomes as readonly string[]).includes(outcome),
  ),
  hullCleaningType: request.outcomes.find(isHullCleaningSubOutcome) ?? "",
  cancelComment: request.cancelComment ?? "",
});

const getApiErrorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error ?? error.response?.data?.message;
    if (typeof message === "string" && message.trim().length > 0) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
};

const HullCleaningTab = () => {
  const theme = useAtomValue(themeAtom);
  const user = useAtomValue(userAtom);
  const setNotifications = useSetAtom(notificationsAtom);
  const { imoNumber, vesselCode, requestId: pathRequestId } = useParams();
  const [searchParams] = useSearchParams();
  const requestId = pathRequestId ?? searchParams.get("requestId") ?? "";
  const [activeBundle, setActiveBundle] = useState<HullCleaningRequestBundle | null>(null);
  const [completedRequests, setCompletedRequests] = useState<HullCleaningRequest[]>([]);
  const [completedEvents, setCompletedEvents] = useState<HullCleaningEvent[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [recordMode, setRecordMode] = useState<RecordMode>("active");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUwOpsRequestsExpanded, setIsUwOpsRequestsExpanded] = useState(true);
  const [uwOpsRequestSearch, setUwOpsRequestSearch] = useState("");
  const [uwOpsStatusFilter, setUwOpsStatusFilter] = useState("all");
  const [uwOpsRequestSortKey, setUwOpsRequestSortKey] = useState<UwOpsRequestSortKey>("requestDate");
  const [uwOpsRequestSortDirection, setUwOpsRequestSortDirection] = useState<"asc" | "desc">("desc");
  const [vendorRecords, setVendorRecords] = useState<HullCleaningVendor[]>([]);
  const [isVendorOptionsLoading, setIsVendorOptionsLoading] = useState(false);
  const [isCreateRequestModalOpen, setIsCreateRequestModalOpen] = useState(false);

  logV("HullCleaningTab - BUILD", { loading: isLoading, mode: recordMode });

  const hasRequestContext = Boolean(requestId || (imoNumber && vesselCode));
  const hasCompletedData = completedRequests.length > 0 || completedEvents.length > 0;
  const currentRequest = activeBundle?.currentRequest ?? null;
  const auditUser = user?.email ?? user?.name ?? undefined;
  const currentStatus = toStatus(currentRequest?.status);
  const hasForm = Boolean(form);
  const showOutcomeSection = currentStatus !== "open";
  const hasEditableWorkflowStatus = currentStatus === "open" || currentStatus === "planned";
  const isBookingEditable = currentStatus === "open" || (currentStatus === "planned" && isEditMode);
  const isOutcomeEditable = currentStatus === "planned" || isEditMode;
  const isCleaningFieldEditable = currentStatus !== "completed";
  const isOutcomeDetailEditable = currentStatus === "planned" || isEditMode;
  const canCancel = currentStatus === "open" || currentStatus === "planned";
  const canCreateRequest = Boolean(imoNumber) && !currentRequest && !isLoading;
  const isPrimaryActionDisabled = !hasEditableWorkflowStatus || isEditMode || isLoading;
  const isCancelActionDisabled = !canCancel || isEditMode || isLoading;
  const isBookingConfirmDisabled =
    isLoading || isEditMode || (currentStatus === "open" ? !isBookingEditable : currentStatus !== "planned");
  const hasInvalidDateOrder = isEarlierDate(form?.cleaningDate, form?.bookedDate);
  const vendorOptions = useMemo(
    () => getVendorOptionsForLocation(vendorRecords, form?.cleaningPort ?? ""),
    [form?.cleaningPort, vendorRecords],
  );
  const loadBundle = useCallback(async () => {
    if (!hasRequestContext) {
      logV("HullCleaningTab - loadBundle - missing context");
      setActiveBundle(null);
      setCompletedRequests([]);
      setCompletedEvents([]);
      setForm(null);
      setError(null);
      setIsEditMode(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      let nextActiveBundle: HullCleaningRequestBundle | null = null;
      let nextCompletedRequests: HullCleaningRequest[] = [];
      let nextCompletedEvents: HullCleaningEvent[] = [];

      if (imoNumber && vesselCode) {
        [nextActiveBundle, nextCompletedRequests, nextCompletedEvents] = await Promise.all([
          getActiveHullCleaningRequestBundleByVessel(imoNumber, vesselCode),
          getCompletedHullCleaningRequestsByVessel(imoNumber, vesselCode),
          getCompletedHullCleaningEventsByVessel(imoNumber, vesselCode),
        ]);
      } else if (requestId) {
        const result = await getHullCleaningRequestBundle(requestId);
        if (toStatus(result.currentRequest.status) === "open" || toStatus(result.currentRequest.status) === "planned") {
          nextActiveBundle = result;
        } else {
          nextCompletedRequests = [result.currentRequest];
          nextCompletedEvents = result.completedEvents;
        }
      } else {
        setActiveBundle(null);
        setCompletedRequests([]);
        setCompletedEvents([]);
        setForm(null);
        setIsEditMode(false);
        return;
      }

      if (!nextActiveBundle && nextCompletedRequests.length === 0 && nextCompletedEvents.length === 0) {
        log("HullCleaningTab - loadBundle - no requests");
        setActiveBundle(null);
        setCompletedRequests([]);
        setCompletedEvents([]);
        setForm(null);
        setIsEditMode(false);
        return;
      }

      log("STATE - loadBundle - hull cleaning", {
        status: nextActiveBundle ? toStatus(nextActiveBundle.currentRequest.status) : "none",
        completed: nextCompletedRequests.length,
      });
      setActiveBundle(nextActiveBundle);
      setCompletedRequests(nextCompletedRequests);
      setCompletedEvents(nextCompletedEvents);
      setRecordMode((current) => {
        if (current === "active" && nextActiveBundle) return "active";
        if (current === "completed" && (nextCompletedRequests.length > 0 || nextCompletedEvents.length > 0))
          return "completed";
        if (nextActiveBundle) return "active";
        if (nextCompletedRequests.length > 0 || nextCompletedEvents.length > 0) return "completed";
        return "active";
      });
      setForm(nextActiveBundle ? buildForm(nextActiveBundle.currentRequest) : null);
      setIsEditMode(false);
    } catch (err) {
      logE(new Error("HullCleaningTab - loadBundle - failed"));
      setError("Unable to load hull cleaning process.");
      toastError("Unable to load hull cleaning process.");
    } finally {
      setIsLoading(false);
    }
  }, [hasRequestContext, imoNumber, requestId, vesselCode]);

  useEffect(() => {
    void loadBundle();
  }, [loadBundle]);

  useEffect(() => {
    if (!showOutcomeSection || !hasForm) {
      setVendorRecords([]);
      return;
    }

    let isCancelled = false;

    setIsVendorOptionsLoading(true);
    getHullCleaningVendors()
      .then((vendors) => {
        if (!isCancelled) {
          setVendorRecords(vendors);
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          setVendorRecords([]);
        }
        logE(new Error("HullCleaningTab - vendors - failed"));
      })
      .finally(() => {
        if (!isCancelled) {
          setIsVendorOptionsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [currentRequest?.requestId, hasForm, showOutcomeSection]);

  const refreshNotifications = useCallback(async () => {
    try {
      const notifications = await getUserNotifications();
      setNotifications(notifications);
    } catch (error) {
      logE(new Error("HullCleaningTab - refreshNotifications - failed"));
    }
  }, [setNotifications]);

  const setField = (field: keyof FormState, value: string) => {
    setForm((current) => (current ? { ...current, [field]: value } : current));
  };

  const setDateField = (field: "bookedDate" | "cleaningDate", date: Date | null) => {
    const value = date ? date.toISOString().slice(0, 10) : "";
    setForm((current) => (current ? { ...current, [field]: value } : current));
  };

  const toggleOutcome = (outcome: HullCleaningOutcome) => {
    setForm((current) => {
      if (!current) return current;
      const isSelected = current.outcomes.includes(outcome);
      const outcomes = isSelected
        ? current.outcomes.filter((item) => item !== outcome)
        : [...current.outcomes, outcome];

      return {
        ...current,
        outcomes,
        hullCleaningType: outcome === hullCleaningOutcome && isSelected ? "" : current.hullCleaningType,
      };
    });
  };

  const selectHullCleaningSubOutcome = (subOutcome: HullCleaningSubOutcome) => {
    setForm((current) => {
      if (!current) return current;

      return {
        ...current,
        hullCleaningType: current.hullCleaningType === subOutcome ? "" : subOutcome,
      };
    });
  };

  const toggleEditMode = () => setIsEditMode((current) => !current);

  const getOutcomeValidationError = (currentForm: FormState): string | null => {
    const missingFields: string[] = [];

    if (!currentForm.cleaningDate) missingFields.push("cleaning date");
    if (!currentForm.cleaningPort.trim()) missingFields.push("cleaning port");
    if (currentForm.outcomes.length === 0) missingFields.push("outcome");
    if (currentForm.outcomes.includes(hullCleaningOutcome) && !currentForm.hullCleaningType) {
      missingFields.push("hull cleaning type");
    }
    if (!currentForm.vendor.trim()) missingFields.push("vendor");

    if (missingFields.length === 0) return null;

    return `Provide ${missingFields.join(", ")} before submitting the outcome.`;
  };

  const submitOutcome = async () => {
    log("HullCleaningTab - submitOutcome", { requestId: currentRequest?.requestId });
    if (!currentRequest || !form) {
      log("HullCleaningTab - submitOutcome - skipped", { reason: "missing context" });
      return;
    }

    if (isEarlierDate(form.cleaningDate, form.bookedDate)) {
      log("HullCleaningTab - submitOutcome - rejected", { reason: "date order" });
      toastError("Cleaning date cannot be earlier than booked date.");
      return;
    }

    const outcomeValidationError = getOutcomeValidationError(form);
    if (outcomeValidationError) {
      log("HullCleaningTab - submitOutcome - rejected", { reason: "outcome fields required" });
      toastError(outcomeValidationError);
      return;
    }

    setIsLoading(true);
    try {
      const result = await submitHullCleaningOutcome({
        requestId: currentRequest.requestId,
        imoNumber: currentRequest.imoNumber,
        vesselCode: currentRequest.vesselCode,
        opsCoordinatorName: form.opsCoordinatorName || undefined,
        cleaningDate: form.cleaningDate,
        cleaningPort: form.cleaningPort,
        vendor: form.vendor,
        vendorRef: form.vendorRef || undefined,
        comments: form.outcomeComment || undefined,
        outcomes: form.outcomes,
        hullCleaningType: form.hullCleaningType || undefined,
        updatedBy: auditUser,
      });
      log("HullCleaningTab - submitOutcome - response", { success: result?.success, status: result?.status });
      setIsEditMode(false);
      toastSuccess("Hull cleaning outcome submitted.");
      await Promise.all([loadBundle(), refreshNotifications()]);
    } catch (err) {
      if (!axios.isCancel(err)) logE(err, { operation: "HullCleaningTab - submitOutcome - failed" });
      toastError(getApiErrorMessage(err, "Unable to submit hull cleaning outcome."));
    } finally {
      setIsLoading(false);
    }
  };

  const confirmEdit = async () => {
    log("HullCleaningTab - confirmEdit", { requestId: currentRequest?.requestId });
    if (!currentRequest || !form) {
      log("HullCleaningTab - confirmEdit - skipped", { reason: "missing context" });
      return;
    }

    if (currentStatus === "planned" && (!form.bookedDate || !form.bookedPort.trim())) {
      log("HullCleaningTab - confirmEdit - rejected", { reason: "booking fields required" });
      toastError("Booked date and booked port are required.");
      return;
    }

    if (isEarlierDate(form.cleaningDate, form.bookedDate)) {
      log("HullCleaningTab - confirmEdit - rejected", { reason: "date order" });
      toastError("Cleaning date cannot be earlier than booked date.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await updateHullCleaningRequest({
        requestId: currentRequest.requestId,
        imoNumber: currentRequest.imoNumber,
        vesselCode: currentRequest.vesselCode,
        opsCoordinatorName: form.opsCoordinatorName || undefined,
        cleaningDate: form.bookedDate || undefined,
        cleaningPort: form.bookedPort || undefined,
        bookingComment: form.voComment || undefined,
        vendor: form.vendor || undefined,
        vendorRef: form.vendorRef || undefined,
        outcomes: form.outcomes.length > 0 ? form.outcomes : undefined,
        updatedBy: auditUser,
        updatedAt: new Date().toISOString(),
      });
      log("HullCleaningTab - confirmEdit - response", { success: result?.success, status: result?.status });
      setIsEditMode(false);
      toastSuccess("Hull cleaning request updated.");
      await Promise.all([loadBundle(), refreshNotifications()]);
    } catch (err) {
      if (!axios.isCancel(err)) logE(err, { operation: "HullCleaningTab - confirmEdit - failed" });
      setIsEditMode(false);
      toastError(getApiErrorMessage(err, "Unable to update hull cleaning request."));
    } finally {
      setIsLoading(false);
    }
  };

  const submitBooking = async () => {
    log("HullCleaningTab - submitBooking", { requestId: currentRequest?.requestId });
    if (!currentRequest || !form) {
      log("HullCleaningTab - submitBooking - skipped", { reason: "missing context" });
      return;
    }
    if (!form.bookedDate || !form.bookedPort.trim()) {
      log("HullCleaningTab - submitBooking - rejected", { reason: "booking fields required" });
      toastError("Booked date and booked port are required.");
      return;
    }
    if (isEarlierDate(form.cleaningDate, form.bookedDate)) {
      log("HullCleaningTab - submitBooking - rejected", { reason: "date order" });
      toastError("Cleaning date cannot be earlier than booked date.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await submitHullCleaningPlanning({
        requestId: currentRequest.requestId,
        imoNumber: currentRequest.imoNumber,
        vesselCode: currentRequest.vesselCode,
        opsCoordinatorName: form.opsCoordinatorName || undefined,
        cleaningDate: form.bookedDate,
        cleaningPort: form.bookedPort,
        bookingComment: form.voComment || undefined,
        updatedBy: auditUser,
      });
      log("HullCleaningTab - submitBooking - response", { success: result?.success, status: result?.status });
      toastSuccess("Hull cleaning booking submitted.");
      await Promise.all([loadBundle(), refreshNotifications()]);
    } catch (err) {
      if (!axios.isCancel(err)) logE(err, { operation: "HullCleaningTab - submitBooking - failed" });
      toastError(getApiErrorMessage(err, "Unable to submit hull cleaning booking."));
    } finally {
      setIsLoading(false);
    }
  };

  const confirmBooking = async () => {
    log("HullCleaningTab - confirmBooking", { status: currentStatus });
    if (!currentRequest) {
      log("HullCleaningTab - confirmBooking - skipped", { reason: "missing context" });
      return;
    }
    if (currentStatus === "open") {
      log("HullCleaningTab - confirmBooking - planning");
      await submitBooking();
      return;
    }

    setIsLoading(true);
    try {
      const result = await confirmHullCleaningBooking({
        requestId: currentRequest.requestId,
        imoNumber: currentRequest.imoNumber,
        vesselCode: currentRequest.vesselCode,
        updatedBy: auditUser,
      });
      log("HullCleaningTab - confirmBooking - response", { success: result?.success, sent: result?.emailSent });
      if (result.emailSent) {
        toastSuccess("Hull cleaning booking confirmation email sent.");
      } else {
        toastWarning("Booking confirmation email was not confirmed. Check backend logs for details.");
      }
    } catch (err) {
      if (!axios.isCancel(err)) logE(err, { operation: "HullCleaningTab - confirmBooking - failed" });
      toastError(getApiErrorMessage(err, "Unable to send hull cleaning booking confirmation email."));
    } finally {
      setIsLoading(false);
    }
  };

  const cancelRequest = async () => {
    log("HullCleaningTab - cancelRequest", { requestId: currentRequest?.requestId });
    if (!currentRequest || !form) {
      log("HullCleaningTab - cancelRequest - skipped", { reason: "missing context" });
      return;
    }
    if (!form.cancelComment.trim()) {
      log("HullCleaningTab - cancelRequest - rejected", { reason: "comment required" });
      toastError("Cancellation comment is required.");
      return;
    }

    let wasCancelled = false;

    setIsLoading(true);
    try {
      const result = await cancelHullCleaningRequest({
        requestId: currentRequest.requestId,
        imoNumber: currentRequest.imoNumber,
        vesselCode: currentRequest.vesselCode,
        cancelComment: form.cancelComment,
        updatedBy: auditUser,
      });
      log("HullCleaningTab - cancelRequest - response", { success: result?.success, status: result?.status });
      wasCancelled = true;
      setIsCancelModalOpen(false);
      toastSuccess("Request is successfully cancelled.");
    } catch (err) {
      if (!axios.isCancel(err)) logE(err, { operation: "HullCleaningTab - cancelRequest - failed" });
      toastError(getApiErrorMessage(err, "Unable to cancel hull cleaning request."));
    } finally {
      setIsLoading(false);
    }

    if (wasCancelled) {
      void Promise.all([loadBundle(), refreshNotifications()]);
    }
  };

  const panelClass = twMerge(
    "rounded-lg p-6 shadow-md",
    theme.home.tailwind.panelBackground,
    theme.home.tailwind.textDefault,
  );
  const inputClass = twMerge(
    "w-full rounded-md border px-3 py-2 text-sm focus:outline-none",
    theme.vesselDetails.info,
    theme.uwhcAssistant.background,
    theme.uwhcAssistant.border,
  );
  const labelClass = twMerge("text-xs font-semibold uppercase", theme.vesselDetails.fields);
  const readOnlyInputClass = twMerge(theme.home.tailwind.backgound, theme.vesselDetails.info);
  const secondarySurfaceClass = twMerge(
    "rounded-md border",
    theme.myVesselsTable.tailwind.tableBorder,
    theme.home.tailwind.backgound,
  );
  const sectionTitleClass = "mb-4 border-b pb-2 text-base font-bold text-green";
  const tableClass = twMerge("min-w-full text-[12px]", theme.vesselDetails.info);
  const tableHeadClass = "text-[#2F7F7B] dark:text-teal-300";
  const tableHeadRowClass = twMerge(
    "border-b",
    theme.myVesselsTable.tailwind.tableBorder,
    theme.uwhcAssistant.chatUi.headerBg,
    theme.uwhcAssistant.chatUi.panelBorder,
  );
  const tableRowClass = twMerge(
    "border-b text-left",
    theme.myVesselsTable.tailwind.tableBorder,
    theme.vesselDetails.info,
  );
  const tableCellClass = twMerge("px-4 py-3 text-sm", theme.vesselDetails.info);
  const requestListScrollClass = twMerge(
    "max-h-[420px] overflow-auto [scrollbar-color:#B7C9C4_#F3F4F4] dark:[scrollbar-color:#4B5563_#1F2937] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-[#F3F4F4] dark:[&::-webkit-scrollbar-track]:bg-gray-800 [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar]:w-2.5",
    theme.uwhcAssistant.tableScroll,
    theme.uwhcAssistant.scrolHover,
  );
  const toggleButtonClass = (isActive: boolean, isDisabled = false) =>
    twMerge(
      "rounded px-3 py-1.5 text-sm font-medium",
      isActive ? theme.home.tailwind.greenBg : twMerge(theme.uwhcAssistant.background, theme.uwhcAssistant.text),
      isActive && "text-white",
      isDisabled && "cursor-not-allowed opacity-50",
    );
  const activeWorkflowRows = (activeBundle?.workflowTable ?? []).filter((row) => {
    const rowStatus = toStatus(row.status);
    return rowStatus === "open" || rowStatus === "planned";
  });
  const displayedWorkflowRows = recordMode === "active" ? activeWorkflowRows : completedRequests;
  const hasAnyHullCleaningData = Boolean(currentRequest || hasCompletedData);
  const hasNoHullCleaningData = !currentRequest && !hasCompletedData;
  const canViewCompleted = hasCompletedData || Boolean(imoNumber);
  const shouldShowUwOpsSections = recordMode === "completed" || (!isLoading && !error && hasNoHullCleaningData);
  const workflowRows = displayedWorkflowRows;
  const visibleWorkflowRows = workflowRows
    .filter((row) => {
      const search = uwOpsRequestSearch.trim().toLowerCase();
      return (
        (uwOpsStatusFilter === "all" || toStatus(row.status) === uwOpsStatusFilter) &&
        (!search ||
          Object.values(row).some((value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(search),
          ))
      );
    })
    .sort((left, right) => {
      const leftValue = String(left[uwOpsRequestSortKey] ?? "");
      const rightValue = String(right[uwOpsRequestSortKey] ?? "");
      const comparison = leftValue.localeCompare(rightValue, undefined, { numeric: true, sensitivity: "base" });
      return uwOpsRequestSortDirection === "asc" ? comparison : -comparison;
    });
  const toggleUwOpsRequestSort = (nextSortKey: UwOpsRequestSortKey) => {
    if (uwOpsRequestSortKey === nextSortKey) {
      setUwOpsRequestSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setUwOpsRequestSortKey(nextSortKey);
    setUwOpsRequestSortDirection("asc");
  };
  const resetUwOpsRequestGrid = () => {
    setUwOpsRequestSearch("");
    setUwOpsStatusFilter("all");
    setUwOpsRequestSortKey("requestDate");
    setUwOpsRequestSortDirection("desc");
  };
  const viewMode = "table";
  const getStatusAppearance = (status?: string | null) => {
    const normalized = (status ?? "").trim().toLowerCase();

    if (normalized === "planned")
      return { background: "bg-[#fde9cf] dark:bg-amber-950/50", text: "text-[#cc8a27] dark:text-amber-300" };
    if (normalized === "completed" || normalized === "complete")
      return { background: "bg-[#dff3e7] dark:bg-emerald-950/50", text: "text-[#2b8a57] dark:text-emerald-300" };
    if (normalized === "cancelled" || normalized === "canceled")
      return { background: "bg-[#f7dfe1] dark:bg-red-950/50", text: "text-[#a33b4a] dark:text-red-300" };

    return { background: "bg-[#e7eefc] dark:bg-blue-950/50", text: "text-[#4568a8] dark:text-blue-300" };
  };
  const renderStatusBadge = (status?: string | null) => {
    const statusAppearance = getStatusAppearance(status);

    return (
      <span
        className={twMerge(
          "inline-block rounded-md px-[10px] py-[5px] text-xs font-bold leading-4",
          statusAppearance.background,
          statusAppearance.text,
        )}
      >
        {status}
      </span>
    );
  };

  const renderInput = (
    label: string,
    value: string,
    onChange: ((value: string) => void) | null,
    readOnly = true,
    placeholder?: string,
  ) => (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>{label}</label>
      <input
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={onChange ? (event) => onChange(event.target.value) : undefined}
        className={twMerge(inputClass, readOnly && readOnlyInputClass)}
      />
    </div>
  );

  const renderTextArea = (
    label: string,
    value: string,
    onChange: ((value: string) => void) | null,
    readOnly = true,
    placeholder?: string,
    spanClass = "md:col-span-2",
  ) => (
    <div className={twMerge("flex flex-col gap-2", spanClass)}>
      <label className={labelClass}>{label}</label>
      <textarea
        value={value}
        readOnly={readOnly}
        placeholder={placeholder}
        onChange={onChange ? (event) => onChange(event.target.value) : undefined}
        className={twMerge(inputClass, "min-h-[88px]", readOnly && readOnlyInputClass)}
      />
    </div>
  );

  const renderStatusField = (label: string, status?: string | null) => (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>{label}</label>
      <div className={twMerge(inputClass, readOnlyInputClass, "flex min-h-[42px] items-center")}>
        {renderStatusBadge(status)}
      </div>
    </div>
  );

  const renderVendorDropdown = () => (
    <div className="flex flex-col gap-2">
      <label className={labelClass}>Vendor</label>
      <Dropdown
        value={form?.vendor ?? ""}
        options={vendorOptions}
        placeholder="Enter or select vendor"
        disabled={!isOutcomeDetailEditable}
        isLoading={isVendorOptionsLoading}
        onChange={(event) => {
          const value = (event.target as HTMLInputElement).value;
          if (typeof value === "string") {
            setField("vendor", value);
          }
        }}
        onSelectOption={(option) => setField("vendor", option)}
        className="[&>input]:min-w-0 [&>input]:rounded-md [&>input]:px-3 [&>input]:py-2 [&>input]:text-sm"
      />
    </div>
  );

  return (
    <LoadingWrapper
      isLoading={isLoading}
      loadingIndicatorClassName={theme.home.tailwind.spinner}
      loadingIndicatorSize="lg"
    >
      <div className="flex flex-col gap-4">
        <div className={panelClass}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>Hull Cleaning</h2>
              <p className={twMerge("mt-1 text-sm", theme.vesselDetails.info)}>
                Review the vessel workflow and create a request when no active request exists.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsCreateRequestModalOpen(true)}
              disabled={!canCreateRequest}
              aria-label="Create hull cleaning request"
              title={currentRequest ? "An active hull cleaning request already exists" : "Create hull cleaning request"}
              className={twMerge(
                "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green text-white shadow-sm transition hover:bg-[#176a5e] focus:outline-none focus-visible:ring-2 focus-visible:ring-green/30 focus-visible:ring-offset-2",
                !canCreateRequest &&
                  "cursor-not-allowed bg-gray-300 text-gray-500 shadow-none hover:bg-gray-300 dark:bg-gray-700",
              )}
              data-test="hull-cleaning-create-request"
            >
              <PlusIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {!hasRequestContext && (
          <div className={panelClass}>
            <p className={twMerge("text-sm", theme.vesselDetails.info)}>
              Open Hull Cleaning from a vessel record to load its process.
            </p>
          </div>
        )}

        {hasRequestContext && error && (
          <div className={twMerge(panelClass, "border border-red-500 text-red-500")}>{error}</div>
        )}

        {hasRequestContext && !error && !isLoading && !hasAnyHullCleaningData && (
          <div className={panelClass}>
            <p className={twMerge("text-sm", theme.vesselDetails.info)}>No hull cleaning requests were found.</p>
          </div>
        )}

        {hasAnyHullCleaningData && (
          <>
            <div className={panelClass}>
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h2 className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>UW Ops Records</h2>
                </div>
                <div className={twMerge("inline-flex rounded-md p-1", secondarySurfaceClass)}>
                  <button
                    type="button"
                    onClick={() => setRecordMode("active")}
                    disabled={!currentRequest}
                    className={toggleButtonClass(recordMode === "active", !currentRequest)}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => setRecordMode("completed")}
                    disabled={!canViewCompleted}
                    className={toggleButtonClass(recordMode === "completed", !canViewCompleted)}
                  >
                    Completed
                  </button>
                </div>
              </div>

              {recordMode === "active" && currentRequest && form ? (
                <>
                  <section className="mb-6">
                    <div className={sectionTitleClass}>Request Details</div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                      {renderInput(
                        "Ops Coordinator",
                        form.opsCoordinatorName,
                        isEditMode ? (value) => setField("opsCoordinatorName", value) : null,
                        !isEditMode,
                      )}
                      {renderInput("Vessel Name", currentRequest.vesselName, null)}
                      {renderInput("IMO Number", currentRequest.imoNumber, null)}
                      {renderStatusField("Status", currentRequest.status)}
                      {renderInput("Request Date", dateOnly(currentRequest.requestDate), null)}
                      {renderInput("Reason", currentRequest.reason ?? "", null)}
                      {renderTextArea("Perf Team Comment", currentRequest.perfComment ?? "", null)}
                    </div>
                  </section>

                  <section className="mb-6">
                    <div className={sectionTitleClass}>Booking Details</div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <div className="flex flex-col gap-2">
                        <label className={labelClass}>Booked Date</label>
                        <Datepicker
                          selected={toDate(form.bookedDate)}
                          onChange={(date) => setDateField("bookedDate", date)}
                          isDisabled={!isBookingEditable}
                          placeholderText="Booked date"
                        />
                      </div>
                      {renderInput(
                        "Booked Port",
                        form.bookedPort,
                        isBookingEditable ? (value) => setField("bookedPort", value) : null,
                        !isBookingEditable,
                        "Enter booked port",
                      )}
                      {renderTextArea(
                        "VO Comment",
                        form.voComment,
                        isBookingEditable ? (value) => setField("voComment", value) : null,
                        !isBookingEditable,
                        "Enter VO comment",
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap justify-start gap-3">
                      <button
                        type="button"
                        onClick={() => void confirmBooking()}
                        disabled={isBookingConfirmDisabled}
                        className={twMerge(
                          "rounded-md bg-[#2d8f89] px-[18px] py-[11px] text-sm font-semibold text-white",
                          isBookingConfirmDisabled && "cursor-not-allowed opacity-50",
                        )}
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (isEditMode) {
                            void confirmEdit();
                            return;
                          }
                          toggleEditMode();
                        }}
                        disabled={!hasEditableWorkflowStatus || isLoading}
                        className={twMerge(
                          "rounded-md border border-[#d7dfdf] bg-[#eef2f2] px-[18px] py-[11px] text-sm font-semibold text-[#466063] dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200",
                          isEditMode &&
                            "border-[#d7dfdf] bg-[#eef2f2] text-[#466063] dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200",
                          (!hasEditableWorkflowStatus || isLoading) && "cursor-not-allowed opacity-50",
                        )}
                      >
                        {isEditMode ? "Confirm Edit" : "Edit"}
                      </button>
                    </div>
                  </section>

                  {showOutcomeSection && (
                    <section>
                      <div className={sectionTitleClass}>UW Ops Outcome Details</div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <div className="flex flex-col gap-2">
                          <label className={labelClass}>UW OPS COMPLETION DATE</label>
                          <Datepicker
                            selected={toDate(form.cleaningDate)}
                            onChange={(date) => setDateField("cleaningDate", date)}
                            isDisabled={!isCleaningFieldEditable}
                            minDate={toDate(form.bookedDate) ?? undefined}
                            placeholderText="Cleaning date"
                          />
                          {hasInvalidDateOrder && (
                            <div className="text-xs text-red-500">
                              Cleaning date cannot be earlier than booked date.
                            </div>
                          )}
                        </div>
                        {renderInput(
                          "UW OPS Port",
                          form.cleaningPort,
                          isCleaningFieldEditable ? (value) => setField("cleaningPort", value) : null,
                          !isCleaningFieldEditable,
                          "Enter Cleaning Port",
                        )}
                        <div className="flex flex-col gap-2 md:col-span-2">
                          <label className={labelClass}>UW OPS Carried Out</label>
                          <div
                            className={twMerge(
                              "grid grid-cols-1 gap-3 rounded-md border p-3",
                              theme.myVesselsTable.tailwind.tableBorder,
                              !isOutcomeEditable && theme.home.tailwind.backgound,
                            )}
                          >
                            {hullCleaningOutcomes.map((outcome) => (
                              <div key={outcome} className="flex flex-col gap-2">
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={form.outcomes.includes(outcome)}
                                    disabled={!isOutcomeEditable}
                                    onChange={() => toggleOutcome(outcome)}
                                  />
                                  <span>{outcome}</span>
                                </label>
                                {outcome === hullCleaningOutcome && form.outcomes.includes(hullCleaningOutcome) && (
                                  <div className="ml-6 grid grid-cols-1 gap-2">
                                    {hullCleaningSubOutcomes.map((subOutcome) => (
                                      <label key={subOutcome} className="flex items-center gap-2 text-sm">
                                        <input
                                          type="checkbox"
                                          checked={form.hullCleaningType === subOutcome}
                                          disabled={!isOutcomeEditable}
                                          onChange={() => selectHullCleaningSubOutcome(subOutcome)}
                                        />
                                        <span>{subOutcome}</span>
                                      </label>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        {renderVendorDropdown()}
                        {renderInput(
                          "Vendor Ref",
                          form.vendorRef,
                          isOutcomeDetailEditable ? (value) => setField("vendorRef", value) : null,
                          !isOutcomeDetailEditable,
                          "Enter Vendor Reference",
                        )}
                        {renderTextArea(
                          "UW Ops Results & Comments",
                          form.outcomeComment,
                          isOutcomeDetailEditable ? (value) => setField("outcomeComment", value) : null,
                          !isOutcomeDetailEditable,
                          "Enter Outcome Comment",
                          "md:col-span-2 xl:col-span-2",
                        )}
                      </div>
                    </section>
                  )}
                </>
              ) : recordMode === "active" ? (
                <p className={twMerge("text-sm", theme.vesselDetails.info)}>
                  No active hull cleaning request for this vessel.
                </p>
              ) : (
                <p className={twMerge("text-sm", theme.vesselDetails.info)}>
                  Completed hull cleaning history for this vessel.
                </p>
              )}
            </div>

            {recordMode === "active" && currentRequest && form && (
              <div className="my-[10px] flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (currentStatus === "planned") {
                      void submitOutcome();
                      return;
                    }

                    void submitBooking();
                  }}
                  disabled={isPrimaryActionDisabled}
                  className={twMerge(
                    "rounded-md bg-[#2d8f89] px-[18px] py-[11px] text-sm font-semibold text-white",
                    isPrimaryActionDisabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  {currentStatus === "planned" ? "Submit Outcome" : "Submit Booking"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsCancelModalOpen(true)}
                  disabled={isCancelActionDisabled}
                  className={twMerge(
                    "rounded-md bg-[#b74848] px-[18px] py-[11px] text-sm font-semibold text-white",
                    isCancelActionDisabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  Cancel Request
                </button>
              </div>
            )}

            {recordMode === "completed" && (
              <div className={panelClass}>
                <WithTooltip
                  id="hull-cleaning-uw-ops-requests-tooltip"
                  content={`${isUwOpsRequestsExpanded ? "Collapse" : "Expand"} UW Ops Requests`}
                  className="w-full"
                  place="top"
                >
                  <button
                    type="button"
                    onClick={() => setIsUwOpsRequestsExpanded((current) => !current)}
                    aria-expanded={isUwOpsRequestsExpanded}
                    aria-label={`${isUwOpsRequestsExpanded ? "Collapse" : "Expand"} UW Ops Requests`}
                    className={twMerge(
                      sectionTitleClass,
                      "-mx-2 flex w-[calc(100%+1rem)] cursor-pointer items-center justify-between gap-3 rounded-t-lg px-2 py-2 text-left transition-colors hover:bg-primary-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-green/30 dark:hover:bg-gray-800",
                    )}
                  >
                    <span>UW Ops Requests</span>
                    <ChevronDownIcon
                      className={twMerge("h-5 w-5 transition-transform", !isUwOpsRequestsExpanded && "-rotate-90")}
                      aria-hidden="true"
                    />
                  </button>
                </WithTooltip>
                {isUwOpsRequestsExpanded && (
                  <>
                    <DataGridToolbar
                      className="mb-4"
                      searchValue={uwOpsRequestSearch}
                      onSearchChange={setUwOpsRequestSearch}
                      searchAriaLabel="Search UW Ops requests"
                      filterValue={uwOpsStatusFilter}
                      onFilterChange={setUwOpsStatusFilter}
                      filterAriaLabel="Filter UW Ops requests by status"
                      allFilterLabel="All statuses"
                      filterOptions={[
                        { value: "completed", label: "Completed" },
                        { value: "cancelled", label: "Cancelled" },
                      ]}
                      resultCount={visibleWorkflowRows.length}
                      totalCount={workflowRows.length}
                      resultLabel="requests"
                      onReset={resetUwOpsRequestGrid}
                      resetAriaLabel="Reset UW Ops Requests grid to its initial state"
                    />
                    {viewMode === "table" ? (
                      <div
                        className={twMerge(
                          "rounded-lg border",
                          requestListScrollClass,
                          theme.myVesselsTable.tailwind.tableBorder,
                        )}
                      >
                        <table className={tableClass}>
                          <thead className={tableHeadClass}>
                            <tr className={tableHeadRowClass}>
                              {uwOpsRequestColumns.map(({ label, sortKey }) => (
                                <th key={label} className="px-4 py-3 text-left text-xs font-semibold uppercase">
                                  {sortKey ? (
                                    <button
                                      type="button"
                                      onClick={() => toggleUwOpsRequestSort(sortKey)}
                                      className="group inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-green/30"
                                    >
                                      {label}
                                      {uwOpsRequestSortKey === sortKey ? (
                                        uwOpsRequestSortDirection === "asc" ? (
                                          <ChevronUpIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                        ) : (
                                          <ChevronDownIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                        )
                                      ) : (
                                        <ArrowsUpDownIcon
                                          className="h-3.5 w-3.5 opacity-35 transition group-hover:opacity-80"
                                          aria-hidden="true"
                                        />
                                      )}
                                    </button>
                                  ) : (
                                    label
                                  )}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {visibleWorkflowRows.map((row) => {
                              return (
                                <tr key={row.requestId} className={tableRowClass}>
                                  <td className={tableCellClass}>{renderStatusBadge(row.status)}</td>
                                  <td className={tableCellClass}>{row.vesselName}</td>
                                  <td className={tableCellClass}>{row.vesselCode}</td>
                                  <td className={tableCellClass}>{row.imoNumber}</td>
                                  <td className={tableCellClass}>{row.opsCoordinatorName ?? "-"}</td>
                                  <td className={tableCellClass}>{dateOnly(row.requestDate) || "-"}</td>
                                  <td className={tableCellClass}>{row.reason || "-"}</td>
                                  <td className={tableCellClass}>{row.perfComment || "-"}</td>
                                  <td className={tableCellClass}>{dateOnly(row.bookingDate) || "-"}</td>
                                  <td className={tableCellClass}>{row.cleaningPort || "-"}</td>
                                  <td className={tableCellClass}>{row.bookingComment || "-"}</td>
                                  <td className={tableCellClass}>{dateOnly(row.cleaningDate) || "-"}</td>
                                  <td className={tableCellClass}>{row.cleaningPort || "-"}</td>
                                  <td className={tableCellClass}>{row.vendor || "-"}</td>
                                  <td className={tableCellClass}>{row.vendorRef || "-"}</td>
                                  <td className={tableCellClass}>{row.outcomes.join(", ") || "-"}</td>
                                </tr>
                              );
                            })}
                            {visibleWorkflowRows.length === 0 && (
                              <tr className={tableRowClass}>
                                <td
                                  colSpan={16}
                                  className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                                >
                                  No matching requests.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className={twMerge("grid gap-4 pr-2 md:grid-cols-2", requestListScrollClass)}>
                        {visibleWorkflowRows.map((row) => {
                          return (
                            <div
                              key={row.requestId}
                              className={twMerge(
                                "rounded-md border p-4",
                                theme.myVesselsTable.tailwind.tableBorder,
                                theme.vesselDetails.info,
                              )}
                            >
                              <div className="mb-3 flex items-center justify-between">
                                <div className={twMerge("text-sm font-semibold", theme.vesselDetails.info)}>
                                  {row.vesselName}
                                </div>
                                {renderStatusBadge(row.status)}
                              </div>
                              <div className="grid gap-3 text-sm md:grid-cols-2">
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Vessel Name</div>
                                  <div>{displayValue(row.vesselName)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Vessel Code</div>
                                  <div>{displayValue(row.vesselCode)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>IMO Number</div>
                                  <div>{displayValue(row.imoNumber)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Ops Coordinator</div>
                                  <div>{displayValue(row.opsCoordinatorName)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Request Date</div>
                                  <div>{dateOnly(row.requestDate) || "-"}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Reason</div>
                                  <div>{displayValue(row.reason)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>
                                    Perf. Team Comment
                                  </div>
                                  <div>{displayValue(row.perfComment)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Booked Date</div>
                                  <div>{dateOnly(row.bookingDate) || "-"}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Booked Port</div>
                                  <div>{displayValue(row.cleaningPort)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>VO Comment</div>
                                  <div>{displayValue(row.bookingComment)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Cleaning Date</div>
                                  <div>{dateOnly(row.cleaningDate) || "-"}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Cleaning Port</div>
                                  <div>{displayValue(row.cleaningPort)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Vendor</div>
                                  <div>{displayValue(row.vendor)}</div>
                                </div>
                                <div>
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Vendor Ref</div>
                                  <div>{displayValue(row.vendorRef)}</div>
                                </div>
                                <div className="md:col-span-2">
                                  <div className={twMerge("text-xs", theme.vesselDetails.fields)}>Outcome</div>
                                  <div>{displayValue(row.outcomes.join(", "))}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <Modal
              isOpen={isCancelModalOpen}
              closeModal={() => !isLoading && setIsCancelModalOpen(false)}
              className="max-w-[640px]"
            >
              <div className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>
                      Cancel Hull Cleaning Request
                    </h3>
                    <p className={twMerge("mt-1 text-sm", theme.vesselDetails.info)}>
                      Enter the cancellation comment before confirming the request cancellation.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsCancelModalOpen(false)}
                    disabled={isLoading}
                    className={twMerge(
                      "inline-flex items-center gap-2 rounded-md border px-5 py-2.5 text-sm font-semibold",
                      theme.myVesselsTable.tailwind.tableBorder,
                      theme.home.tailwind.panelBackground,
                      theme.home.tailwind.textDefault,
                      isLoading && "cursor-not-allowed opacity-50",
                    )}
                  >
                    <span aria-hidden="true">x</span>
                    <span>Cancel</span>
                  </button>
                </div>

                <div className={twMerge("text-sm", theme.vesselDetails.info)}>
                  Add a cancellation comment before cancelling this hull cleaning request.
                </div>

                <div className={twMerge("whitespace-pre-line text-sm leading-6", theme.vesselDetails.info)}>
                  {`Cancellation to be opted only if
vessel is being redelivered/lump
sum agreement/vessel going for DD
next voyage.

Service unavailability/uncertain
voyage prospects - please update
the booking details as per the
available info. If in doubt, please
reach out to performance team`}
                </div>

                {form && (
                  <div className="flex flex-col gap-2">
                    <label className={labelClass}>Cancellation Comment</label>
                    <textarea
                      value={form.cancelComment}
                      onChange={(event) => setField("cancelComment", event.target.value)}
                      placeholder="Enter cancellation comment"
                      className={twMerge(inputClass, "min-h-[120px]")}
                    />
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsCancelModalOpen(false)}
                    disabled={isLoading}
                    className={twMerge(
                      "rounded-md border px-5 py-2.5 text-sm font-semibold",
                      theme.myVesselsTable.tailwind.tableBorder,
                      theme.home.tailwind.panelBackground,
                      theme.home.tailwind.textDefault,
                      isLoading && "cursor-not-allowed opacity-50",
                    )}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => void cancelRequest()}
                    disabled={isLoading}
                    className={twMerge(
                      "rounded-md bg-rose-700 px-5 py-2.5 text-sm font-semibold text-white",
                      isLoading && "cursor-not-allowed opacity-50",
                    )}
                  >
                    Confirm Cancellation
                  </button>
                </div>
              </div>
            </Modal>
          </>
        )}

        {imoNumber && shouldShowUwOpsSections && <UwOpsSections imoNumber={imoNumber} />}

        <HullCleaningRequestModal
          open={isCreateRequestModalOpen}
          imoNumber={imoNumber ?? ""}
          onCancel={() => setIsCreateRequestModalOpen(false)}
          onCreated={(response) => {
            setIsCreateRequestModalOpen(false);
            setRecordMode("active");
            toastSuccess(`Hull cleaning request ${response.requestId} submitted successfully.`);
            void loadBundle();
          }}
        />

        <PageFooter />
        <ToastContainer />
      </div>
    </LoadingWrapper>
  );
};

export default HullCleaningTab;
